import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { IconButton } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";

import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import { formatMoney } from "utils/Utils";
import { endpoint } from "assets/api/endpoint";
import { apiHandler } from "assets/api";

import CircularProgresss from "components/CircularProgress/CircularProgresss";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import ColumnGroupingTable from "components/UpdateUi/Table/Table";
import ToastModal from "views/components/ToastModal";

import {
  diamondMasterData,
  toastAction,
} from "assets/properties/properties";

const DiamondMaster = ({ featureFlagState }) => {
  const navigate = useNavigate();

  const [selectedTableData, setSelectedTableData] = useState([]);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");
  const [callInProgress, setCallInProgress] = useState(false);

  const { authToken } = useSelector((state) => state.login.authData);

  const handleCreateSubmit = () => {
    navigate(diamondMasterData?.createUrl);
  };

  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
  };

  const getActionIcons = (id) => (
    <IconButton
      aria-label="Edit Diamond Master"
      onClick={() =>
        navigate(`/coreservice/diamondMasterCreate/${id}`)
      }
    >
      <EditIcon />
    </IconButton>
  );

  const parseIndicatorData = (data) => {
    const reportData = (Array.isArray(data) ? data : []).map((item) => ({
      caretColor: item?.caretColor ?? "",
      caretGrade: item?.caretGrade ?? "",
      minAmt:
        item?.minAmt != null ? formatMoney(item.minAmt) : "",
      maxAmt:
        item?.maxAmt != null ? formatMoney(item.maxAmt) : "",
      action: getActionIcons(item?.id),
    }));
    const colourOrder = [
      "D", "E", "F", "G", "H",
      "I", "J", "K", "L", "M",
    ];

    reportData.sort((a, b) => {
      const indexA = colourOrder.indexOf(
        String(a.caretColor).toUpperCase()
      );
      const indexB = colourOrder.indexOf(
        String(b.caretColor).toUpperCase()
      );

      return (
        (indexA === -1 ? Infinity : indexA) -
        (indexB === -1 ? Infinity : indexB)
      );
    });

    setSelectedTableData(reportData);
  };

  const GetData = useCallback(async () => {
    setCallInProgress(true);

    try {
      const result = await apiHandler({
        url: endpoint.DIAMOND_MASTER_FIND_ALL,
        authToken,
      });

      if (result?.data?.error_code) {
        toastUpdate(
          toastAction?.error,
          result?.data?.user_description || "Unable to fetch Diamond Master data.",
          true
        );
        return;
      }

      parseIndicatorData(result?.data);
    } catch (error) {
      toastUpdate(
        toastAction?.error,
        error?.message || "Unable to fetch Diamond Master data.",
        true
      );
    } finally {
      setCallInProgress(false);
    }
  }, [authToken]);

  useEffect(() => {
    GetData();
  }, [GetData]);

  return (
    <div>
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <SubHeader
            label={diamondMasterData?.label}
            button1={diamondMasterData?.button}
            button1click={handleCreateSubmit}
          />
        </GridItem>

        <GridItem xs={12} sm={12} md={12}>
          <ColumnGroupingTable
            columns={diamondMasterData?.tableColumns}
            rows={selectedTableData}
          />
        </GridItem>

        {toast && (
          <ToastModal
            toastMessage={toastMessage}
            toastMessageType={toastMessageType}
            toastUpdate={toastUpdate}
          />
        )}

        {callInProgress && (
          <CircularProgresss callInProgress={callInProgress} />
        )}
      </GridContainer>
    </div>
  );
};

DiamondMaster.propTypes = {
  featureFlagState: PropTypes.object,
};

export default DiamondMaster;